import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import { Button } from '@/components/common/Button';
import type { AssiBackgammon, ControlloBackgammon, SceltaAssi } from '@/lib/limulo/sfidaBackgammon';

/**
 * Il testo di un controllo noto (I-459). Una `t('…')` statica per ogni controllo, MAI una chiave costruita a runtime:
 * `verify-lingue` legge solo le statiche, e un testo mancante in una lingua deve far rosso.
 */
function nomeControllo(t: TFunction<'general'>, controllo: ControlloBackgammon): string {
  switch (controllo) {
    case 'unlimited':
      return t('social.sfida_bg_orologio_unlimited', { defaultValue: 'No clock' });
    case 'blitz':
      return t('social.sfida_bg_orologio_blitz', { defaultValue: 'Blitz' });
    case 'rapid':
      return t('social.sfida_bg_orologio_rapid', { defaultValue: 'Rapid' });
    case 'classic':
      return t('social.sfida_bg_orologio_classic', { defaultValue: 'Classic' });
  }
}

/**
 * Le DUE scelte della sfida a backgammon, al posto della fila di modalita' (I-459): «Lunghezza» (in punti) e
 * «Orologio». Mostra SOLO i valori che il server serve (`assi`, da `assiDelBackgammon`), e ogni tocco passa da
 * `scegliSuAsse`: la coppia e' sempre una voce servita. Nessuna `key` si costruisce qui: la manda chi legge `scelta`.
 * I bottoni sono `scelta` come quelli della fila di prima, con la stessa area di tocco.
 */
export function SfidaAssiBackgammon({
  assi,
  scelta,
  onScegli,
  disabled,
}: {
  assi: AssiBackgammon;
  scelta: SceltaAssi;
  onScegli: (asse: 'lunghezza' | 'controllo', valore: number | ControlloBackgammon) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('general');
  const idLunghezza = useId();
  const idOrologio = useId();
  return (
    <div className="mb-4 flex flex-col gap-3" data-testid="sfida-bg-assi">
      <div role="group" aria-labelledby={idLunghezza}>
        <p id={idLunghezza} className="mb-1.5 text-xs font-semibold text-fg-muted">
          {t('social.sfida_bg_lunghezza', { defaultValue: 'Match length (points)' })}
        </p>
        <div className="flex flex-wrap gap-2">
          {assi.lunghezze.map((n) => (
            <Button
              key={n}
              variant="scelta"
              tono="incavo"
              peso="bold"
              pressed={scelta.lunghezza === n}
              onClick={() => onScegli('lunghezza', n)}
              disabled={disabled}
              className="after:h-9"
            >
              {n}
            </Button>
          ))}
        </div>
      </div>
      <div role="group" aria-labelledby={idOrologio}>
        <p id={idOrologio} className="mb-1.5 text-xs font-semibold text-fg-muted">
          {t('social.sfida_bg_orologio', { defaultValue: 'Clock' })}
        </p>
        <div className="flex flex-wrap gap-2">
          {assi.controlli.map((c) => (
            <Button
              key={c}
              variant="scelta"
              tono="incavo"
              peso="bold"
              pressed={scelta.controllo === c}
              onClick={() => onScegli('controllo', c)}
              disabled={disabled}
              className="after:h-9"
            >
              {nomeControllo(t, c)}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
}
